import { create } from 'zustand';
import { toast } from 'sonner';
import { UPLOAD_FAILED_TOAST_ACTION, UPLOAD_MAX_BYTES, uploadFailedToastTitle, uploadTooLargeSentence } from './pro-builder-copy';
import { sendProBuilderDocument, type ProBuilderUploadResult } from './pro-builder-upload-request';

export type UploadRowState = 'queued' | 'uploading' | 'done' | 'failed';

export interface UploadRow {
  id: number;
  /** Held only until the row finishes (done or failed), so finished rows do not keep file contents for the tab session. */
  file: File | null;
  /** The file's original name (the row's `data-file`). */
  name: string;
  /** The app folder this row was enqueued for; an app switch mid-queue makes the server refuse it. */
  folder: string;
  state: UploadRowState;
  progress: number;
  /** `Saved`, `Saved as <name>`, or the refusal sentence; empty while waiting or sending. */
  detail: string;
  /** The name the server actually wrote (`brief-2.pdf` when `brief.pdf` was taken); null until done. */
  savedName: string | null;
  /** True once Claude Code has been told about this saved file (`pro-builder-upload-notice.ts`). */
  announced: boolean;
}

/** New rows for a pick or a drop; a file over the limit is born failed and is never sent. */
export function makeUploadRows(files: readonly File[], folder: string, firstId: number): UploadRow[] {
  return files.map((file, i) => {
    const tooLarge = file.size > UPLOAD_MAX_BYTES;
    return {
      id: firstId + i,
      file: tooLarge ? null : file,
      name: file.name,
      folder,
      state: tooLarge ? 'failed' : 'queued',
      progress: 0,
      detail: tooLarge ? uploadTooLargeSentence(file.name) : '',
      savedName: null,
      announced: false,
    };
  });
}

/** The row to send next: the first queued one, and only while nothing is uploading (one at a time, in order). */
export function nextRowToSend(rows: readonly UploadRow[]): UploadRow | null {
  if (rows.some((r) => r.state === 'uploading')) return null;
  return rows.find((r) => r.state === 'queued') ?? null;
}

export function patchRow(rows: readonly UploadRow[], id: number, patch: Partial<UploadRow>): UploadRow[] {
  return rows.map((r) => (r.id === id ? { ...r, ...patch } : r));
}

export function savedDetail(result: Pick<ProBuilderUploadResult, 'name' | 'renamed'>): string {
  return result.renamed ? `Saved as ${result.name}` : 'Saved';
}

type Sender = typeof sendProBuilderDocument;
let sender: Sender = sendProBuilderDocument;
/** Test seam. */
export function setUploadSenderForTest(next: Sender | null): void {
  sender = next ?? sendProBuilderDocument;
}

interface ProBuilderUploadStore {
  open: boolean;
  rows: UploadRow[];
  nextId: number;
  openDialog: () => void;
  closeDialog: () => void;
  enqueue: (files: readonly File[], folder: string) => void;
  markAnnounced: (ids: readonly number[]) => void;
}

export const useProBuilderUploadStore = create<ProBuilderUploadStore>((set, get) => {
  // A file dropped on the terminal uploads with the dialog closed, so its failure would otherwise go unseen.
  const reportFailure = (row: Pick<UploadRow, 'id' | 'name' | 'detail'>): void => {
    if (get().open) return;
    toast.error(uploadFailedToastTitle(row.name), {
      id: `pro-builder-upload-failed-${row.id}`,
      description: row.detail,
      action: { label: UPLOAD_FAILED_TOAST_ACTION, onClick: () => set({ open: true }) },
    });
  };
  const pump = (): void => {
    const row = nextRowToSend(get().rows);
    if (!row || !row.file) return;
    set((s) => ({ rows: patchRow(s.rows, row.id, { state: 'uploading', progress: 0 }) }));
    void sender({
      file: row.file,
      folder: row.folder,
      onProgress: (progress) => set((s) => ({ rows: patchRow(s.rows, row.id, { progress }) })),
    })
      .then(
        (result) => set((s) => ({ rows: patchRow(s.rows, row.id, { state: 'done', progress: 100, file: null, detail: savedDetail(result), savedName: result.name }) })),
        (err: unknown) => {
          const detail = err instanceof Error ? err.message : 'The upload failed. Try again.';
          set((s) => ({ rows: patchRow(s.rows, row.id, { state: 'failed', file: null, detail }) }));
          reportFailure({ id: row.id, name: row.name, detail });
        },
      )
      .then(pump);
  };
  return {
    open: false,
    rows: [],
    nextId: 1,
    openDialog: () => set({ open: true }),
    closeDialog: () => set({ open: false }),
    enqueue: (files, folder) => {
      if (files.length === 0) return;
      const made = makeUploadRows(files, folder, get().nextId);
      set((s) => ({ rows: [...s.rows, ...made], nextId: s.nextId + made.length }));
      for (const row of made) if (row.state === 'failed') reportFailure(row);
      pump();
    },
    markAnnounced: (ids) => set((s) => ({ rows: s.rows.map((r) => (ids.includes(r.id) ? { ...r, announced: true } : r)) })),
  };
});

/** The terminal view not showing (window hidden, or another view) closes the upload dialog, so it cannot pop open again on its own when the terminal returns. */
export function closeUploadDialogWhenHidden(terminalShown: boolean): void {
  if (!terminalShown) useProBuilderUploadStore.getState().closeDialog();
}
